import { useReducer } from 'react'

/**
 * State for the Hockey Canada integration card.
 */
export interface HockeyCanadaState {
  /** Whether the integration is connected on the account */
  isConnected: boolean
  /** Whether the integration is currently enabled */
  isEnabled: boolean
  /** Whether the cost-confirmation modal is visible */
  showWarning: boolean
  /** Enable value awaiting confirmation in the modal */
  pendingEnabled: boolean
}

type HockeyCanadaAction =
  | { type: 'SET_STATUS'; payload: { connected: boolean; enabled: boolean } }
  | { type: 'SET_IS_ENABLED'; payload: boolean }
  | { type: 'SET_SHOW_WARNING'; payload: boolean }
  | { type: 'SET_PENDING_ENABLED'; payload: boolean }
  | { type: 'RESET_WARNING_STATE' }

const initialState: HockeyCanadaState = {
  isConnected: false,
  isEnabled: false,
  showWarning: false,
  pendingEnabled: false,
}

const hockeyCanadaReducer = (
  state: HockeyCanadaState,
  action: HockeyCanadaAction
): HockeyCanadaState => {
  switch (action.type) {
    case 'SET_STATUS':
      return {
        ...state,
        isConnected: action.payload.connected,
        isEnabled: action.payload.enabled,
      }

    case 'SET_IS_ENABLED':
      return { ...state, isEnabled: action.payload }

    case 'SET_SHOW_WARNING':
      return { ...state, showWarning: action.payload }

    case 'SET_PENDING_ENABLED':
      return { ...state, pendingEnabled: action.payload }

    case 'RESET_WARNING_STATE':
      return { ...state, showWarning: false, pendingEnabled: false }

    default:
      return state
  }
}

/**
 * Reducer hook for Hockey Canada integration state.
 *
 * Scoped to this integration rather than added to the shared
 * `useIntegrationsReducer`, so the card stays self-contained.
 */
export const useHockeyCanadaReducer = () => {
  const [state, dispatch] = useReducer(hockeyCanadaReducer, initialState)

  const actions = {
    setStatus: (connected: boolean, enabled: boolean) =>
      dispatch({ type: 'SET_STATUS', payload: { connected, enabled } }),

    setIsEnabled: (isEnabled: boolean) =>
      dispatch({ type: 'SET_IS_ENABLED', payload: isEnabled }),

    setShowWarning: (showWarning: boolean) =>
      dispatch({ type: 'SET_SHOW_WARNING', payload: showWarning }),

    setPendingEnabled: (pendingEnabled: boolean) =>
      dispatch({ type: 'SET_PENDING_ENABLED', payload: pendingEnabled }),

    resetWarningState: () => dispatch({ type: 'RESET_WARNING_STATE' }),
  }

  return { state, actions }
}
